import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { J } from '~/common/constants/keyCodes';
import EcModal from '~/components/Shared/EcModal/EcModal';
import QuickSearchModal from '~/components/Shared/QuickSearchModal/QuickSearchModal';
import { useDisableBodyScroll } from '~/eds/src/hooks/useDisableBodyScroll';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { QueryEntityType } from '~/enums/QueryEntityType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { slices } from '~/redux/slices';
import search from '~/redux/slices/search';
import { useRouting } from '~/routing/useRouting';
import { MODAL_AUTOCOMPLETE, MODAL_QUICK_SEARCH } from '~/types/modal.types';

import { useHasFeatureFlag } from './useHasFeatureFlag';

export const useSearchModal = (): [React.ReactNode, () => void] => {
  const intl = useIntl();
  const hasSearchV2Feature = useHasFeatureFlag(FeatureFlagType.SearchV2);
  const hasSearchV3 = useFlag(FlagType.SearchV3);
  const { location, navigate } = useRouting();
  const [modal, setModal] = useState<string | null>(null);
  useDisableBodyScroll(modal === MODAL_QUICK_SEARCH);

  const { pathname } = location;

  const handleShowModal = () => {
    setModal(hasSearchV2Feature ? MODAL_QUICK_SEARCH : MODAL_AUTOCOMPLETE);
  };

  const handleHideModal = () => {
    setModal(null);
  };

  const handleOnSearchShortcutPress = (event: KeyboardEvent) => {
    if (event.keyCode === J && event.metaKey) {
      handleShowModal();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleOnSearchShortcutPress, false);

    return () => {
      document.removeEventListener(
        'keydown',
        handleOnSearchShortcutPress,
        false,
      );
    };
  }, []);

  const dispatch = useDispatch();

  const navigateTo = (path: string) => {
    if (pathname !== path) navigate(path);
  };

  const searchOnSearchV3 = (query: string) => {
    dispatch(slices.searchV3.actions.reset());
    dispatch(slices.searchV3.actions.setSearchText(query));
  };

  const goToAnalyzerPage = (query: string) => {
    dispatch(
      slices.analyzer.actions.setData({
        filters: [{ entity: QueryEntityType.BoolTextSearch, query }],
      }),
    );

    navigateTo('/analyzer/results');
  };

  const goToSearchPage = (query: string) => {
    if (hasSearchV3) {
      searchOnSearchV3(query);
    } else {
      dispatch(search.actions.clearUnsupportedQuery());
      dispatch(search.actions.setBooleanQuery(query));
      dispatch(
        search.actions.setQuery({
          booleanQuery: query,
          filters: [],
          fields: {},
        }),
      );
      dispatch(search.actions.clearFilterValues());
    }
    navigateTo('/search');
  };

  const handleOnShowMoreClick = (query: string) => {
    if (hasSearchV2Feature) {
      goToSearchPage(query);
    } else {
      goToAnalyzerPage(query);
    }

    handleHideModal();
  };

  const handleOnLinkClick = (documentId: number) => {
    const href = `/document/${documentId}`;
    href.startsWith('http') ? window.open(href, '_blank') : navigate(href);
  };

  const renderSearchOverlay = () => {
    return (
      <QuickSearchModal
        isVisible={modal === MODAL_QUICK_SEARCH}
        hideModal={handleHideModal}
        onSearchSubmit={goToSearchPage}
      />
    );
  };

  const renderSearchModal = () => {
    return (
      <EcModal
        modalType={MODAL_AUTOCOMPLETE}
        title={intl.formatMessage({
          id: 'useSearchModal.searchTitle',
          defaultMessage: 'Search',
          description: 'Title for the search modal',
        })}
        width="720px"
        toViewDocument={true}
        hideModal={handleHideModal}
        handleOnSearchItemClick={handleOnLinkClick}
        handleOnShowMoreClick={handleOnShowMoreClick}
      />
    );
  };

  const searchModal = (
    <>
      {modal === MODAL_QUICK_SEARCH && renderSearchOverlay()}
      {modal === MODAL_AUTOCOMPLETE && renderSearchModal()}
    </>
  );

  return [searchModal, handleShowModal];
};
